import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  Tooltip,
  styled,
  tableCellClasses,
  Pagination,
} from '@mui/material';
import { ReactComponent as EditIcon } from '../../assets/svg/edit.svg';
import { ReactComponent as Trash } from '../../assets/svg/trash.svg';
import { ReactComponent as Approval } from '../../assets/svg/approval.svg';
import { createLanguage, deleteLanguage, getAllLanguages, updateLanguage } from '../../api/language';
import CommonDrawer from '../../CommonComponents/CommonDrawer';
import CommonConfirmationModal from '../../CommonComponents/CommonConfirmationModal';
import { showErrorToast, showSuccessToast } from '../../toastUtils';
import { useUser } from '../../components/UserContext';
import ChangeRequestViewModal from '../../CommonComponents/ChangeRequestViewModal';
import { getApprovalById } from '../../api/approval';
import CommonHeader from '../../CommonComponents/CommonHeader';
import SearchInput from '../../CommonComponents/SearchInput';
import ButtonCommon from '../../CommonComponents/ButtonCommon';
import { Add } from '@mui/icons-material';
import SelectCommon from '../../CommonComponents/SelectCommon';
import ComponentWrapper from '../../CommonComponents/ComponentWrapper';


const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#F9FAFB",
    color: "#000000",
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const LanguagePage = () => {
  const { currentUser } = useUser();
  // const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    // isActive: true,
  });
  const [currentLanguage, setCurrentLanguage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [languageToDelete, setLanguageToDelete] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [paginatedData, setPaginatedData] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [statusValue, setStatusValue] = useState('all');
  const [filterData, setFilteredData] = useState([])
  const [viewLanguage, setViewLanguage] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [approvalLoader, setApprovalLoader] = useState(false);

  useEffect(() => {
    if (currentLanguage) {
      setFormData({
        code: currentLanguage.code || '',
        name: currentLanguage.name || '',
        isActive: currentLanguage.isActive || true,
      });
    } else {
      resetForm();
    }
  }, [currentLanguage]);

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      isActive: true,
    });
    setErrors({});
  };

  const fetchLanguages = async () => {
    try {
      setLoading(true);
      const response = await getAllLanguages(page, perPage);
      if (response.success) {
        // setLanguages(response.data);
        setPaginatedData(response?.data?.data);
        setFilteredData(response?.data?.data);
        setTotalCount(response?.data?.total_count || 0);
      } else {
        showErrorToast(response.message || 'Failed to fetch languages');
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      showErrorToast(error.message || 'Failed to fetch languages');
      setPaginatedData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLanguages();

    // eslint-disable-next-line
  }, [page, perPage]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Language code is required';
    } else if (!/^[a-zA-Z]{2,3}$/.test(formData.code)) {
      newErrors.code = 'Language code must be 2 or 3 letters with no spaces';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Language name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddLanguage = () => {
    setCurrentLanguage(null);
    setOpenDrawer(true);
  };

  const handleEditLanguage = (language) => {
    setCurrentLanguage(language); // Save full language object, not just ID
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setCurrentLanguage(null);
    setIsEdited(false)
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: name === 'isActive' ? checked : value,
    }));

    // Clear error for the field being edited
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    setIsEdited(true)
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitLoading(true);

      const payload = {
        language_code: formData.code,
        language_description: formData.name,
      };

      let response;
      if (currentLanguage?.language_id) {
        // Update existing language
        response = await updateLanguage(currentLanguage.language_id, payload);
      } else {
        // Create new language
        response = await createLanguage(payload);
      }

      if (response.success) {
        // showSuccessToast(currentLanguage ? 'Language updated successfully' : 'Language created successfully');
        showSuccessToast(response.message);
        handleCloseDrawer();
        fetchLanguages();
      } else {
        showErrorToast(response.message);
      }
    } catch (error) {
      console.error('Error submitting language:', error);
      showErrorToast(error.message);
    } finally {
      setSubmitLoading(false);
      setIsEdited(false)
    }
  };

  const handleDeleteClick = (language) => {
    setLanguageToDelete(language);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!languageToDelete?.language_id) {
      showErrorToast('Invalid language selected for deletion.');
      return;
    }

    try {
      setDeleteLoading(true);

      const response = await deleteLanguage(languageToDelete.language_id);
      console.log('Delete response:', response); // 🔥 ADD THIS TOO

      if (response.success) {
        showSuccessToast(response.message);
        fetchLanguages();
      } else {
        showErrorToast(response.message);
      }
    } catch (error) {
      console.error('Error deleting language:', error); // 🔥
      showErrorToast(error.message);
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setLanguageToDelete(null);
    }
  };



  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setLanguageToDelete(null);
  };

  // Add view handler
  const handleViewClick = async (language) => {
    setApprovalLoader(language.approval_id);
    try {
      const response = await getApprovalById(language.approval_id);

      if (response.success) {
        const approvalData = response.data;

        setViewLanguage({
          ...approvalData,
          // requested_by: approvalData.requested_by || "Unknown",
          // requested_at: approvalData.requested_at || new Date().toISOString(),
        });

        setViewModalOpen(true);
      } else {
        console.error('No data received for language approval.');
      }
    } catch (error) {
      console.error('Error fetching language approval details:', error);
    } finally {
      setApprovalLoader(null);
    }
  };

  useEffect(() => {
    const data = paginatedData ?? [];

    const filtered = data.filter((item) => {
      const search = searchValue?.toLowerCase() || '';
      const language = item?.language_description?.toLowerCase() || '';
      const isNameMatch = language.includes(search);

      const isStatusMatch =
        statusValue?.toLowerCase() === 'all' ||
        item?.status?.toLowerCase() === statusValue?.toLowerCase();

      return isNameMatch && isStatusMatch;
    });

    setFilteredData(filtered);
  }, [searchValue, statusValue, paginatedData]);


  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box >
        <Typography sx={{ color: "#525455", mb: 1, fontSize: "0.875rem" }}>
          Language Code
          <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
        </Typography>
        <TextField
          fullWidth
          name="code"
          size='small'
          value={formData.code}
          onChange={handleChange}
          error={!!errors.code}
          helperText={errors.code}
          margin="normal"
          sx={{ m: 0 }}
        />
      </Box>
      <Box>
        <Typography sx={{ color: "#525455", mb: 1, fontSize: "0.875rem" }}>
          Language Name
          <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
        </Typography>
        <TextField
          fullWidth
          size='small'
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={!!errors.name}
          helperText={errors.name}
          margin="normal"
          sx={{ m: 0 }}
        />
      </Box>
      {/* <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.isActive}
              onChange={handleChange}
              name="isActive"
              color="primary"
            />
          }
          label="Active"
        />
      </Grid> */}
    </Box>
  );

  return (
    <ComponentWrapper>
      {/* <Stack sx={{ maxWidth: '1000px' }}> */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', mb: 3 }}>
        <CommonHeader
          title="Master Language"
          subTitle="You can review, audit and change the status of master language on this page."
        />
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'end', flexDirection: 'row', mb: 2 }}>
          <SearchInput
            placeholder="Search by Language Name"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <SelectCommon
            label="Status"
            value={statusValue}
            onChange={(e) => setStatusValue(e.target.value)}
            options={[
              { label: "All", value: "all" },
              { label: "Active", value: "active" },
              // { label: "Inactive", value: "inactive" },
              { label: "Draft", value: "draft" },
            ]}
          />
          <ButtonCommon icon={<Add />} onClick={handleAddLanguage}>
            Add Language
          </ButtonCommon>
        </Box>
      </Box>

      <TableContainer component={Paper}
        variant="outlined"
        sx={{ borderRadius: "12px" }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Language Code</StyledTableCell>
              <StyledTableCell>Language Name</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : (filterData && filterData.length > 0 ? (
              filterData.map((language) => (
                <TableRow key={language.language_id || language.approval_id} hover>
                  <StyledTableCell>{language.language_code}</StyledTableCell>
                  <StyledTableCell>{language.language_description}</StyledTableCell>
                  <StyledTableCell
                    sx={{
                      textTransform: "capitalize",
                      color:
                        language.status === "active"
                          ? "#00B894"
                          : language.status === "inactive"
                            ? "#FF946D"
                            : "#6994FF",
                      fontWeight: 600,
                    }}
                  >
                    {language.status}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {currentUser?.zoneinfo === "super_admin" && <Tooltip title="Approval" arrow>
                      <IconButton
                        size="small"
                        disabled={
                          language.status !== "draft" ||
                          currentUser?.zoneinfo === "product_user"
                        }
                        sx={{
                          color: (theme) =>
                            language.status !== "draft" ||
                              currentUser?.zoneinfo === "product_user"
                              ? theme.palette.grey[400]
                              : "inherit",
                        }}
                        onClick={() => handleViewClick(language)}
                      >
                        {/* {console.log(approvalLoader, language.approval_id)} */}
                        {(language.status === 'draft' && approvalLoader === language.approval_id) ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Approval />
                        )}
                      </IconButton>
                    </Tooltip>}

                    <Tooltip title="Edit" arrow>
                      <IconButton
                        disabled={language.status === "draft" || language.approval_id}
                        sx={{
                          color: (theme) =>
                            language.status === "draft"
                              ? theme.palette.grey[400]
                              : "inherit",
                        }}
                        onClick={() => handleEditLanguage(language)} >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete" arrow>
                      <IconButton
                        disabled={language.status === "draft" || language.approval_id}
                        sx={{
                          color: (theme) =>
                            language.status === "draft"
                              ? theme.palette.grey[400]
                              : "inherit",
                        }}
                        onClick={() => handleDeleteClick(language)}>
                        <Trash />
                      </IconButton>
                    </Tooltip>
                  </StyledTableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No languages found
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
        <Pagination
          count={Math.ceil(totalCount / perPage)}
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
          shape="rounded"
          variant="outlined"
        />
      </Box>

      {/* Use CommonDrawer instead of custom LanguageDrawer */}
      <CommonDrawer
        open={openDrawer}
        onClose={handleCloseDrawer}
        title={currentLanguage ? "Edit Language" : "Add New Language"}
        onSubmit={handleSubmit}
        primaryButton={{
          label: submitLoading ?
            <CircularProgress size={24} color="inherit" /> :
            (currentLanguage ? "Update" : "Create"),
          disabled: submitLoading || !isEdited
        }}
        secondaryButton={{
          disabled: submitLoading
        }}
        drawerWidth={500}
      >
        {drawerContent}
      </CommonDrawer>

      <CommonConfirmationModal
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        description={`Are you sure you want to delete the language?`}
        loading={deleteLoading}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      <ChangeRequestViewModal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        fetchData={fetchLanguages}
        title={`Language ${viewLanguage?.action_type} request`}
        approvalId={viewLanguage?.approval_id}
        beforeData={viewLanguage?.data_before ? {
          name: viewLanguage?.data_before?.language_description,
          code: viewLanguage?.data_before?.language_code
        } : null}
        afterData={{
          name: viewLanguage?.data_after?.language_description,
          code: viewLanguage?.data_after?.language_code
        }}
        requestedBy={viewLanguage?.requested_by}
        requestedAt={viewLanguage?.requested_at}
        actionType={viewLanguage?.action_type}
      // onApprove={() => handleApproveLanguage(viewLanguage)}
      // onReject={() => handleRejectLanguage(viewLanguage)}
      />
      {/* </Stack> */}
    </ComponentWrapper>

  );
};

export default LanguagePage;
